import type { JSONDocNode } from '@atlaskit/editor-json-transformer';

import { getSourceUrlCandidatesFromPromptText } from './sources/configuration';
import type { ManagedBlockSource } from './sources/types';

type JsonADFNode = {
	attrs?: {
		url?: string;
	};
	content?: JsonADFNode[];
	text?: string;
	type?: string;
	version?: number;
};

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const createTextContent = (text: string): JsonADFNode[] =>
	text
		.split('\n')
		.flatMap((line, index) => [
			...(index > 0 ? [{ type: 'hardBreak' } as JsonADFNode] : []),
			...(line.length > 0 ? [{ type: 'text', text: line } as JsonADFNode] : []),
		]);

const createInlineCardContent = (url: string): JsonADFNode => ({
	type: 'inlineCard',
	attrs: {
		url,
	},
});

const createPromptContent = (
	prompt: string,
	sourceEntries: ManagedBlockSource[],
): { consumedSourceUrls: Set<string>; content: JsonADFNode[] } => {
	const sourceUrls = Array.from(new Set(sourceEntries.map((source) => source.url))).sort(
		(left, right) => right.length - left.length,
	);

	if (sourceUrls.length === 0) {
		return {
			consumedSourceUrls: new Set(),
			content: createTextContent(prompt),
		};
	}

	const sourceUrlPattern = new RegExp(sourceUrls.map(escapeRegExp).join('|'), 'g');
	const content: JsonADFNode[] = [];
	const consumedSourceUrls = new Set<string>();
	let previousIndex = 0;

	for (const match of prompt.matchAll(sourceUrlPattern)) {
		const url = match[0];
		const matchIndex = match.index ?? 0;

		content.push(...createTextContent(prompt.slice(previousIndex, matchIndex)));
		content.push(createInlineCardContent(url));
		consumedSourceUrls.add(url);
		previousIndex = matchIndex + url.length;
	}

	content.push(...createTextContent(prompt.slice(previousIndex)));

	return { consumedSourceUrls, content };
};

export const createPromptAdf = (
	prompt: string,
	sourceEntries: ManagedBlockSource[],
): JSONDocNode => {
	const { consumedSourceUrls, content: paragraphContent } = createPromptContent(
		prompt,
		sourceEntries,
	);

	sourceEntries.forEach((source) => {
		if (consumedSourceUrls.has(source.url)) {
			return;
		}

		if (paragraphContent.length > 0) {
			paragraphContent.push({ type: 'text', text: ' ' } as JsonADFNode);
		}
		paragraphContent.push(createInlineCardContent(source.url));
	});

	return {
		type: 'doc',
		version: 1,
		content: [
			{
				type: 'paragraph',
				...(paragraphContent.length > 0 ? { content: paragraphContent } : {}),
			},
		],
	} as JSONDocNode;
};

export const getSourceUrlsFromPromptAdf = (adf: JSONDocNode | undefined): string[] => {
	const sourceUrls: string[] = [];
	const seenUrls = new Set<string>();

	const visit = (node: JsonADFNode): void => {
		if (node.type === 'inlineCard' && node.attrs?.url) {
			const [sourceUrl] = getSourceUrlCandidatesFromPromptText(node.attrs.url);

			if (sourceUrl && !seenUrls.has(sourceUrl)) {
				seenUrls.add(sourceUrl);
				sourceUrls.push(sourceUrl);
			}
		}

		node.content?.forEach(visit);
	};

	if (adf) {
		visit(adf as JsonADFNode);
	}

	return sourceUrls;
};
